import { useEffect, useState } from 'react'

import { Info, Plus } from 'lucide-react'

import { locationTypeCopy } from '@/components/site-setup/locationTypeCopy'
import type { SiteLocationFormValues } from '@/components/site-setup/SiteLocationFormDialog'
import SiteLocationFormDialog from '@/components/site-setup/SiteLocationFormDialog'
import SiteLocationTable from '@/components/site-setup/SiteLocationTable'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useProject } from '@/contexts/ProjectContext'
import { usePageTitle } from '@/hooks/usePageTitle'
import type {
  SiteLocationDto,
  SiteLocationStatusDto,
  SiteLocationTypeDto,
} from '@/services/siteLocationService'
import {
  SiteLocationConflictError,
  siteLocationService,
} from '@/services/siteLocationService'
import { logger } from '@/utils/logger'

import './SiteSetup.css'

const log = logger.scoped('SiteSetup')

type Lookups =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'loaded'
      types: SiteLocationTypeDto[]
      statuses: SiteLocationStatusDto[]
    }

type LocationsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; locations: SiteLocationDto[] }

/** The open dialog: a new location of a type, or an existing one. */
type DialogTarget = { typeId: number; location?: SiteLocationDto }

function SiteSetup() {
  usePageTitle('Site setup')
  const { currentProject } = useProject()
  const projectId = currentProject?.id
  const [lookups, setLookups] = useState<Lookups>({ status: 'loading' })
  const [state, setState] = useState<LocationsState>({ status: 'loading' })
  const [dialog, setDialog] = useState<DialogTarget | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  // Types and statuses are company-wide: load once, not per project.
  useEffect(() => {
    const controller = new AbortController()

    siteLocationService
      .getTypes(controller.signal)
      .then(async (types) => {
        const perType = await Promise.all(
          types.map((type) =>
            siteLocationService.getStatuses(type.id, controller.signal),
          ),
        )
        setLookups({ status: 'loaded', types, statuses: perType.flat() })
      })
      .catch((error: unknown) => {
        // An abort is this component unmounting, not a failure -- surfacing
        // it as one would flash an error on every fast navigation.
        if (controller.signal.aborted) return
        log.error('Failed to load site location types', { error })
        setLookups({ status: 'error' })
      })

    return () => controller.abort()
  }, [])

  // Locations belong to the selected project: reload when it changes, or when
  // a conflict says the list is stale.
  useEffect(() => {
    const controller = new AbortController()

    siteLocationService
      .list({}, controller.signal)
      .then((locations) => setState({ status: 'loaded', locations }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        log.error('Failed to load site locations', { error })
        setState({ status: 'error' })
      })

    return () => controller.abort()
  }, [projectId, reloadKey])

  function upsert(saved: SiteLocationDto) {
    setState((current) => {
      if (current.status !== 'loaded') return current
      const exists = current.locations.some((l) => l.id === saved.id)
      return {
        status: 'loaded',
        locations: exists
          ? current.locations.map((l) => (l.id === saved.id ? saved : l))
          : [...current.locations, saved],
      }
    })
  }

  /** Resolves when saved (the dialog closes); a rejection keeps it open. */
  async function handleSave(
    { typeId, location }: DialogTarget,
    values: SiteLocationFormValues,
  ) {
    setNotice(null)

    if (!location) {
      try {
        const created = await siteLocationService.create({
          typeId,
          name: values.name,
        })
        upsert(created)
        // New locations start Planned; any other choice is a follow-up change.
        if (values.statusId !== created.statusId) {
          try {
            upsert(
              await siteLocationService.changeStatus(
                created.id,
                values.statusId,
                created.rowVersion,
              ),
            )
          } catch (error) {
            log.error('Failed to set status on new site location', { error })
            setNotice(
              `${created.name} was added, but its status could not be set. Edit it to try again.`,
            )
          }
        }
      } catch (error) {
        log.error('Failed to create site location', { typeId, error })
        throw error
      }
      return
    }

    try {
      let saved = location
      if (values.name !== location.name) {
        saved = await siteLocationService.update(location.id, {
          name: values.name,
          rowVersion: saved.rowVersion,
        })
        upsert(saved)
      }
      if (values.statusId !== location.statusId) {
        saved = await siteLocationService.changeStatus(
          location.id,
          values.statusId,
          saved.rowVersion,
        )
        upsert(saved)
      }
    } catch (error) {
      if (error instanceof SiteLocationConflictError) {
        // Someone else changed the record: show theirs, and say so.
        setNotice(
          `${location.name} was changed by someone else. The list has been reloaded — review it and try again.`,
        )
        setReloadKey((key) => key + 1)
        return
      }
      log.error('Failed to update site location', { id: location.id, error })
      throw error
    }
  }

  const failed = lookups.status === 'error' || state.status === 'error'
  const loading =
    !failed && (lookups.status === 'loading' || state.status === 'loading')

  return (
    <div className="willfade">
      <div className="pagehead">
        <div>
          <h1>Site setup</h1>
        </div>
      </div>

      {loading ? (
        <div className="site-setup__loading" aria-busy="true">
          <span className="sr-only">Loading site locations</span>
          {[0, 1, 2].map((row) => (
            <Skeleton key={row} className="h-10 w-full rounded-lg" />
          ))}
        </div>
      ) : null}

      {failed ? (
        <p className="site-setup__message site-setup__message--error">
          Site locations could not be loaded. Try again shortly.
        </p>
      ) : null}

      {notice ? (
        <p role="status" className="site-setup__message">
          {notice}
        </p>
      ) : null}

      {lookups.status === 'loaded' && state.status === 'loaded' ? (
        <>
          {lookups.types.map((type, index) => {
            const copy = locationTypeCopy(type.name)
            const statuses = lookups.statuses.filter(
              (status) => status.typeId === type.id,
            )
            const locations = state.locations.filter(
              (location) => location.typeId === type.id,
            )
            const activeCount = locations.filter(
              (location) =>
                statuses.find((status) => status.id === location.statusId)
                  ?.kind === 'Active',
            ).length
            const isLast = index === lookups.types.length - 1

            return (
              <section key={type.id}>
                <div
                  className={
                    index === 0
                      ? 'site-setup__section-head'
                      : 'site-setup__section-head site-setup__section-head--blocks'
                  }
                >
                  <span className="site-setup__section-title">
                    {copy.section}{' '}
                    <span className="text-(--fg3)">{`${activeCount} active`}</span>
                  </span>
                  <Button onClick={() => setDialog({ typeId: type.id })}>
                    <Plus aria-hidden="true" />
                    {copy.add}
                  </Button>
                </div>

                {locations.length === 0 ? (
                  <p className="site-setup__message">{copy.empty}</p>
                ) : (
                  <div className="site-setup__table-card">
                    <SiteLocationTable
                      header={copy.header}
                      locations={locations}
                      statuses={statuses}
                      onEdit={(location) =>
                        setDialog({ typeId: type.id, location })
                      }
                    />
                    {isLast ? (
                      <p className="site-setup__table-footer">
                        <Info
                          aria-hidden="true"
                          className="size-3.5 shrink-0"
                        />
                        Yards relate to blocks, and the build sequence
                        references them — placement, congestion &amp; shortage
                        timing all read from this layer.
                      </p>
                    ) : null}
                  </div>
                )}
              </section>
            )
          })}

          {dialog ? (
            <SiteLocationFormDialog
              key={dialog.location?.id ?? `new-${dialog.typeId}`}
              typeName={
                lookups.types.find((type) => type.id === dialog.typeId)
                  ?.name ?? ''
              }
              statuses={lookups.statuses.filter(
                (status) => status.typeId === dialog.typeId,
              )}
              location={dialog.location}
              allLocations={state.locations}
              onSave={(values) => handleSave(dialog, values)}
              onClose={() => setDialog(null)}
            />
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export default SiteSetup
